import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { useActivation } from "../../../hooks/useActivation";
import { api } from "../../../lib/auth";
import DeepSpaceBackground from "../../../components/northstar/DeepSpaceBackground";

export default function JourneyPlayer() {
  const { journeyId } = useParams();
  const { data: activation, loading } = useActivation();
  const [definition, setDefinition] = useState(null);
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!journeyId) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/northstar/journeys/${journeyId}`);
        if (!alive) return;
        setDefinition(data.definition);
        if (data.state) {
          setState(data.state);
        } else {
          const started = await api.post(`/northstar/journeys/${journeyId}/start`);
          if (alive) setState(started.data.state);
        }
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || "Journey unavailable.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [journeyId]);

  if (loading) return null;
  if (!activation) return <Navigate to="/portal" replace />;
  if (!activation.northstar_access_granted) return <Navigate to="/northstar" replace />;

  const currentIdx = definition && state
    ? definition.steps.findIndex((s) => s.id === state.current_step_id)
    : -1;
  const currentStep = currentIdx >= 0 ? definition.steps[currentIdx] : null;

  const advance = async () => {
    if (!currentStep || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post(
        `/northstar/journeys/${journeyId}/advance`,
        { from_step_id: currentStep.id },
      );
      setState(data.state);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not advance.");
    } finally {
      setBusy(false);
    }
  };

  const total = definition?.steps?.length || 0;
  const stepNum = currentIdx + 1;
  const isCompletion = state?.status === "completed" || currentStep?.kind === "completion";

  return (
    <div className="relative min-h-screen text-white" data-testid="journey-player">
      <DeepSpaceBackground />
      <main className="relative z-10 max-w-2xl mx-auto px-6 py-10">
        <Link to="/northstar" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6" data-testid="journey-back">
          <ArrowLeft className="h-4 w-4" /> Northstar
        </Link>
        {definition && (
          <>
            <h1 className="text-3xl font-semibold mb-2" data-testid="journey-title">{definition.title}</h1>
            <p className="text-white/70 text-sm mb-6" data-testid="journey-purpose">{definition.purpose}</p>
            {total > 0 && (
              <div className="mb-6" data-testid="journey-progress">
                <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#7ED493] transition-all"
                    style={{ width: `${Math.max(0, ((state?.completed_step_ids?.length || 0) / total) * 100)}%` }}
                  />
                </div>
                <div className="text-xs text-white/50 mt-2">
                  Step {stepNum} of {total}
                </div>
              </div>
            )}
            {currentStep && (
              <div
                className="rounded-2xl border border-white/15 bg-white/[0.04] p-6 mb-6"
                data-testid={`journey-step-${currentStep.id}`}
              >
                <h2 className="text-xl font-medium mb-3 flex items-center gap-2">
                  {isCompletion ? <CheckCircle2 className="h-5 w-5 text-[#7ED493]" /> : <Sparkles className="h-5 w-5 text-white/60" />}
                  {currentStep.title}
                </h2>
                <p className="text-white/85 text-sm leading-relaxed" data-testid="journey-step-body">{currentStep.body}</p>
              </div>
            )}
            {error && (
              <div className="text-sm text-red-300 mb-4" role="alert" data-testid="journey-error">{error}</div>
            )}
            {!isCompletion && currentStep && (
              <button
                onClick={advance}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-full bg-[#7ED493] text-black px-5 py-2.5 text-sm font-medium disabled:opacity-50"
                data-testid="journey-advance-btn"
              >
                {busy ? "…" : "Continue"} <ArrowRight className="h-4 w-4" />
              </button>
            )}
            {isCompletion && (
              <Link
                to={currentStep?.cta?.route || "/northstar"}
                className="inline-flex items-center gap-2 rounded-full bg-[#7ED493] text-black px-5 py-2.5 text-sm font-medium"
                data-testid="journey-completion-cta"
              >
                {currentStep?.cta?.label || "Return to Northstar"}
              </Link>
            )}
          </>
        )}
      </main>
    </div>
  );
}
